/**
 * Audio files of a case or of the lake, and playing one fetched from the files route. That route sits behind
 * the login, so a player gets the file as a blob, and it answers every file as
 * `application/octet-stream`, which Safari will not play: the blob is given the audio type its
 * suffix names before it becomes an object URL. Pure.
 */

/** A container the platform stores a recording in; the server takes exactly these. */
export type AudioContainer = 'webm' | 'm4a' | 'ogg' | 'mp3' | 'wav' | 'flac' | 'amr'

/** Every suffix a file of one of the containers may carry; a 3GP file is of MP4's family. */
const CONTAINER_OF_SUFFIX: ReadonlyMap<string, AudioContainer> = new Map([
  ['webm', 'webm'],
  ['m4a', 'm4a'],
  ['mp4', 'm4a'],
  ['3gp', 'm4a'],
  ['ogg', 'ogg'],
  ['oga', 'ogg'],
  ['opus', 'ogg'],
  ['mp3', 'mp3'],
  ['wav', 'wav'],
  ['flac', 'flac'],
  ['amr', 'amr'],
])

/** The audio type a stored file of each container plays as. */
const PLAYBACK_TYPE: Readonly<Record<AudioContainer, string>> = {
  webm: 'audio/webm',
  m4a: 'audio/mp4',
  ogg: 'audio/ogg',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  flac: 'audio/flac',
  amr: 'audio/amr',
}

/**
 * The file types of a case or lake document that are audio: what an upload takes, the WebM a
 * browser's recorder writes and the 3GP and AMR of an older phone among them. The server's list
 * is `AUDIO_FILE_TYPES` in `spectre.core.audio`. Wider than the containers above: an upload may
 * be `.aac`, which no recording is stored as.
 */
const AUDIO_FILE_TYPES: ReadonlySet<string> = new Set([
  'mp3',
  'wav',
  'm4a',
  'ogg',
  'opus',
  'flac',
  'aac',
  'webm',
  '3gp',
  'amr',
])

/** The same as the suffixes a file picker is given: `.mp3`, `.wav`, … */
export const AUDIO_FILE_SUFFIXES: readonly string[] = Array.from(
  AUDIO_FILE_TYPES,
  type => `.${type}`
)

/** Whether a document of this file type (its extension, in any case) is an audio file. */
export const isAudioFileType = (fileType: string | null | undefined): boolean =>
  AUDIO_FILE_TYPES.has((fileType ?? '').toLowerCase())

/** The lower-cased suffix of a file name or of a URL's path; '' without one. */
function suffixOf(nameOrUrl: string): string {
  const path = nameOrUrl.split(/[?#]/)[0]
  const name = path.slice(path.lastIndexOf('/') + 1)
  const dot = name.lastIndexOf('.')
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase()
}

/** The container a file name or a URL names by its suffix; null for any other. */
export const audioContainerOf = (nameOrUrl: string): AudioContainer | null =>
  CONTAINER_OF_SUFFIX.get(suffixOf(nameOrUrl)) ?? null

/**
 * The fetched bytes typed as the audio their URL names, so every browser plays them; as they
 * came when the URL names no container.
 */
export function asPlayable(blob: Blob, audioUrl: string): Blob {
  const container = audioContainerOf(audioUrl)
  const type = container ? PLAYBACK_TYPE[container] : null
  return type && blob.type !== type ? new Blob([blob], { type }) : blob
}
